/**
 * Email Automation Page
 * AIDLC Administrative Frontend
 *
 * Manage email automation rules: create, edit, enable/disable, test, delete.
 * Rules fire on system events (backlog changes, agent status, escalations, etc.)
 * and send templated emails via the configured sending agent.
 *
 * Personas: ops
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getAutomationRules,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  toggleAutomationRule,
  testAutomationRule,
  getTemplates,
} from '../../services/emailTemplateService';
import type {
  EmailAutomationRule,
  EmailTriggerEvent,
  EmailTemplate,
} from '../../types/email';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { Badge } from '../shared/Badge';
import apiClient from '../../services/api';

// ── Constants ─────────────────────────────────────────────────────

const TRIGGER_EVENTS: { value: EmailTriggerEvent; label: string }[] = [
  { value: 'backlog:status_change', label: 'Backlog: Status Change' },
  { value: 'agent:task_completed', label: 'Agent: Task Completed' },
  { value: 'agent:status_change', label: 'Agent: Status Change' },
  { value: 'escalation:created', label: 'Escalation: Created' },
  { value: 'sprint:completed', label: 'Sprint: Completed' },
  { value: 'recurring:schedule', label: 'Recurring: Schedule' },
  { value: 'manual', label: 'Manual' },
];

const TRIGGER_CONDITION_OPTIONS: Record<string, { key: string; label: string; options?: string[] }[]> = {
  'backlog:status_change': [
    { key: 'newStatus', label: 'New Status', options: ['open', 'in-progress', 'review', 'done', 'blocked'] },
    { key: 'oldStatus', label: 'Old Status', options: ['open', 'in-progress', 'review', 'done', 'blocked'] },
    { key: 'priority', label: 'Priority', options: ['critical', 'high', 'medium', 'low'] },
  ],
  'agent:task_completed': [
    { key: 'agentId', label: 'Agent ID' },
    { key: 'taskType', label: 'Task Type' },
  ],
  'agent:status_change': [
    { key: 'newStatus', label: 'New Status', options: ['available', 'working', 'idle', 'offline'] },
    { key: 'agentId', label: 'Agent ID' },
  ],
  'escalation:created': [
    { key: 'severity', label: 'Severity', options: ['critical', 'high', 'medium', 'low'] },
    { key: 'category', label: 'Category' },
  ],
  'sprint:completed': [
    { key: 'projectId', label: 'Project ID' },
  ],
  'recurring:schedule': [],
  'manual': [],
};

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getTriggerBadgeVariant(event: EmailTriggerEvent): 'info' | 'success' | 'danger' | 'warning' | 'default' {
  if (event.startsWith('backlog:')) return 'info';
  if (event.startsWith('agent:')) return 'success';
  if (event.startsWith('escalation:')) return 'danger';
  if (event.startsWith('sprint:')) return 'warning';
  if (event === 'recurring:schedule') return 'info';
  return 'default';
}

function formatTimestamp(iso?: string): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Blank Rule Factory ────────────────────────────────────────────

type RuleFormData = Omit<EmailAutomationRule, 'id' | 'createdAt' | 'updatedAt' | 'totalSent' | 'lastTriggeredAt' | 'createdBy'>;

function blankRule(): RuleFormData {
  return {
    name: '',
    description: '',
    enabled: true,
    triggerEvent: 'manual',
    triggerConditions: {},
    templateId: '',
    sendAsAgentId: undefined,
    recipientType: 'fixed',
    fixedRecipients: [],
    dynamicRecipientField: undefined,
    scheduleType: 'immediate',
    scheduleTime: '09:00',
    scheduleDayOfWeek: 1,
  };
}

// ── Agent Type ────────────────────────────────────────────────────

interface AgentOption {
  id: string;
  name: string;
}

// ── Main Component ────────────────────────────────────────────────

export function EmailAutomationPage() {
  const [rules, setRules] = useState<EmailAutomationRule[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<EmailAutomationRule | null>(null);
  const [formData, setFormData] = useState<RuleFormData>(blankRule());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Multi-email input
  const [emailInput, setEmailInput] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<EmailAutomationRule | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Test/preview
  const [testTarget, setTestTarget] = useState<EmailAutomationRule | null>(null);
  const [testResult, setTestResult] = useState<{
    rule: { id: string; name: string };
    preview: { subject: string; body: string };
    variables: Record<string, string>;
    wouldSendTo: string[] | string;
  } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  // ── Load Data ─────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesData, templatesData, agentsResp] = await Promise.all([
        getAutomationRules(),
        getTemplates(),
        apiClient.get('/agents').then((r) => r.data).catch(() => []),
      ]);
      setRules(rulesData);
      setTemplates(templatesData);
      setAgents(
        Array.isArray(agentsResp)
          ? agentsResp.map((a: { id?: string; agentId?: string; name?: string; displayName?: string }) => ({
              id: a.id || a.agentId || '',
              name: a.name || a.displayName || a.id || '',
            }))
          : []
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load automation rules';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Toggle Rule ───────────────────────────────────────────────

  const handleToggle = useCallback(async (rule: EmailAutomationRule) => {
    try {
      const updated = await toggleAutomationRule(rule.id);
      setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch {
      setError('Failed to toggle rule. Please try again.');
    }
  }, []);

  // ── Open Create/Edit Modal ────────────────────────────────────

  const openCreateModal = useCallback(() => {
    setEditingRule(null);
    setFormData(blankRule());
    setEmailInput('');
    setFormError(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((rule: EmailAutomationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      triggerEvent: rule.triggerEvent,
      triggerConditions: { ...rule.triggerConditions },
      templateId: rule.templateId,
      sendAsAgentId: rule.sendAsAgentId,
      recipientType: rule.recipientType,
      fixedRecipients: [...rule.fixedRecipients],
      dynamicRecipientField: rule.dynamicRecipientField,
      scheduleType: rule.scheduleType,
      scheduleTime: rule.scheduleTime,
      scheduleDayOfWeek: rule.scheduleDayOfWeek,
    });
    setEmailInput('');
    setFormError(null);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingRule(null);
    setFormData(blankRule());
    setFormError(null);
    setEmailInput('');
  }, []);

  // ── Save Rule ─────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!formData.name.trim()) {
      setFormError('Rule name is required.');
      return;
    }
    if (!formData.templateId) {
      setFormError('Please select a template.');
      return;
    }
    if (formData.recipientType === 'fixed' && formData.fixedRecipients.length === 0) {
      setFormError('At least one recipient email is required for fixed recipients.');
      return;
    }
    if (formData.recipientType === 'dynamic' && !formData.dynamicRecipientField?.trim()) {
      setFormError('Dynamic recipient field is required.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editingRule) {
        const updated = await updateAutomationRule(editingRule.id, formData);
        setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      } else {
        const created = await createAutomationRule(formData);
        setRules((prev) => [...prev, created]);
      }
      closeModal();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save rule';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  }, [formData, editingRule, closeModal]);

  // ── Delete Rule ───────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAutomationRule(deleteTarget.id);
      setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      setError('Failed to delete rule. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  // ── Test Rule ─────────────────────────────────────────────────

  const handleTest = useCallback(async (rule: EmailAutomationRule) => {
    setTestTarget(rule);
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const result = await testAutomationRule(rule.id);
      setTestResult(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Test failed';
      setTestError(msg);
    } finally {
      setTesting(false);
    }
  }, []);

  const closeTestModal = useCallback(() => {
    setTestTarget(null);
    setTestResult(null);
    setTestError(null);
  }, []);

  // ── Add Email to Fixed Recipients ─────────────────────────────

  const addRecipientEmail = useCallback(() => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (formData.fixedRecipients.includes(email)) {
      setEmailInput('');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      fixedRecipients: [...prev.fixedRecipients, email],
    }));
    setEmailInput('');
  }, [emailInput, formData.fixedRecipients]);

  const removeRecipientEmail = useCallback((email: string) => {
    setFormData((prev) => ({
      ...prev,
      fixedRecipients: prev.fixedRecipients.filter((e) => e !== email),
    }));
  }, []);

  // ── Update Trigger Condition ──────────────────────────────────

  const updateCondition = useCallback((key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      triggerConditions: { ...prev.triggerConditions, [key]: value },
    }));
  }, []);

  const removeCondition = useCallback((key: string) => {
    setFormData((prev) => {
      const next = { ...prev.triggerConditions };
      delete next[key];
      return { ...prev, triggerConditions: next };
    });
  }, []);

  // ── Render ────────────────────────────────────────────────────

  if (loading) {
    return <LoadingSpinner message="Loading automation rules..." />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Automation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create rules that automatically send emails based on system events
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 text-sm bg-ldaja-blue text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Create Rule
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="text-center py-16">
          <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">No automation rules yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create your first rule to automate email notifications</p>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 text-sm bg-ldaja-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Rule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left: info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-sm font-semibold text-ldaja-black truncate">{rule.name}</h3>
                    <Badge variant={getTriggerBadgeVariant(rule.triggerEvent)} dot>
                      {TRIGGER_EVENTS.find((t) => t.value === rule.triggerEvent)?.label || rule.triggerEvent}
                    </Badge>
                    {!rule.enabled && (
                      <Badge variant="neutral">Disabled</Badge>
                    )}
                  </div>
                  {rule.description && (
                    <p className="text-sm text-gray-500 mb-2 line-clamp-2">{rule.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>
                      Last triggered: {formatTimestamp(rule.lastTriggeredAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                      </svg>
                      {rule.totalSent} sent
                    </span>
                    {rule.recipientType === 'fixed' && rule.fixedRecipients.length > 0 && (
                      <span>
                        To: {rule.fixedRecipients.slice(0, 2).join(', ')}
                        {rule.fixedRecipients.length > 2 && ` +${rule.fixedRecipients.length - 2}`}
                      </span>
                    )}
                    {rule.recipientType === 'dynamic' && (
                      <span>Dynamic: {rule.dynamicRecipientField}</span>
                    )}
                  </div>
                </div>

                {/* Right: toggle + actions */}
                <div className="flex items-center gap-3 shrink-0">
                  {/* Toggle switch */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rule.enabled}
                      onChange={() => handleToggle(rule)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:bg-ldaja-blue transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
                  </label>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleTest(rule)}
                      className="p-1.5 text-gray-400 hover:text-ldaja-blue hover:bg-blue-50 rounded transition-colors"
                      title="Test rule"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => openEditModal(rule)}
                      className="p-1.5 text-gray-400 hover:text-ldaja-blue hover:bg-blue-50 rounded transition-colors"
                      title="Edit rule"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setDeleteTarget(rule)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Delete rule"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create/Edit Modal ──────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ldaja-black">
                {editingRule ? 'Edit Automation Rule' : 'Create Automation Rule'}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Name & Description */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Notify CEO on escalation"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Optional description of what this rule does"
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Trigger Event */}
              <div>
                <label className="block text-sm font-semibold text-ldaja-black mb-1">Trigger Event</label>
                <select
                  value={formData.triggerEvent}
                  onChange={(e) => {
                    const event = e.target.value as EmailTriggerEvent;
                    setFormData((p) => ({
                      ...p,
                      triggerEvent: event,
                      triggerConditions: {},
                      // Reset schedule fields for non-recurring triggers
                      ...(event !== 'recurring:schedule' ? { scheduleType: 'immediate' } : {}),
                    }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                >
                  {TRIGGER_EVENTS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {/* Trigger Conditions */}
              {(TRIGGER_CONDITION_OPTIONS[formData.triggerEvent] ?? []).length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-2">Trigger Conditions</label>
                  <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                    {(TRIGGER_CONDITION_OPTIONS[formData.triggerEvent] ?? []).map((cond) => (
                      <div key={cond.key} className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-600 w-28 shrink-0">{cond.label}</span>
                        {cond.options ? (
                          <select
                            value={formData.triggerConditions[cond.key] ?? ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                updateCondition(cond.key, e.target.value);
                              } else {
                                removeCondition(cond.key);
                              }
                            }}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                          >
                            <option value="">Any</option>
                            {cond.options.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={formData.triggerConditions[cond.key] ?? ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                updateCondition(cond.key, e.target.value);
                              } else {
                                removeCondition(cond.key);
                              }
                            }}
                            placeholder="Leave blank for any"
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                          />
                        )}
                      </div>
                    ))}
                    <p className="text-[11px] text-gray-400 mt-1">Leave blank to match any value for that condition.</p>
                  </div>
                </div>
              )}

              {/* Template Selector */}
              <div>
                <label className="block text-sm font-semibold text-ldaja-black mb-1">Email Template</label>
                <select
                  value={formData.templateId}
                  onChange={(e) => setFormData((p) => ({ ...p, templateId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.category})
                    </option>
                  ))}
                </select>
              </div>

              {/* Sending Agent Selector */}
              <div>
                <label className="block text-sm font-semibold text-ldaja-black mb-1">Send As Agent</label>
                <select
                  value={formData.sendAsAgentId ?? ''}
                  onChange={(e) => setFormData((p) => ({ ...p, sendAsAgentId: e.target.value || undefined }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                >
                  <option value="">Default (system)</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">The agent whose persona will sign the email.</p>
              </div>

              {/* Recipient Type */}
              <div>
                <label className="block text-sm font-semibold text-ldaja-black mb-2">Recipient Type</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="recipientType"
                      checked={formData.recipientType === 'fixed'}
                      onChange={() => setFormData((p) => ({ ...p, recipientType: 'fixed' }))}
                      className="text-ldaja-blue focus:ring-ldaja-blue"
                    />
                    <span className="text-sm text-gray-700">Fixed recipients</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="recipientType"
                      checked={formData.recipientType === 'dynamic'}
                      onChange={() => setFormData((p) => ({ ...p, recipientType: 'dynamic' }))}
                      className="text-ldaja-blue focus:ring-ldaja-blue"
                    />
                    <span className="text-sm text-gray-700">Dynamic (from event data)</span>
                  </label>
                </div>
              </div>

              {/* Fixed Recipients */}
              {formData.recipientType === 'fixed' && (
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-1">Recipients</label>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addRecipientEmail();
                        }
                      }}
                      placeholder="email@example.com"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={addRecipientEmail}
                      className="px-3 py-2 text-sm bg-ldaja-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.fixedRecipients.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {formData.fixedRecipients.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeRecipientEmail(email)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic Recipient Field */}
              {formData.recipientType === 'dynamic' && (
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-1">Dynamic Recipient Field</label>
                  <input
                    type="text"
                    value={formData.dynamicRecipientField ?? ''}
                    onChange={(e) => setFormData((p) => ({ ...p, dynamicRecipientField: e.target.value }))}
                    placeholder="e.g., assignee.email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    JSON path within the event payload to extract the recipient email address.
                  </p>
                </div>
              )}

              {/* Schedule Section (recurring:schedule only) */}
              {formData.triggerEvent === 'recurring:schedule' && (
                <div>
                  <label className="block text-sm font-semibold text-ldaja-black mb-2">Schedule</label>
                  <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Frequency</label>
                      <select
                        value={formData.scheduleType ?? 'immediate'}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            scheduleType: e.target.value as 'immediate' | 'daily' | 'weekly',
                          }))
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                      >
                        <option value="immediate">Immediate</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>

                    {(formData.scheduleType === 'daily' || formData.scheduleType === 'weekly') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                        <input
                          type="time"
                          value={formData.scheduleTime ?? '09:00'}
                          onChange={(e) => setFormData((p) => ({ ...p, scheduleTime: e.target.value }))}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                        />
                      </div>
                    )}

                    {formData.scheduleType === 'weekly' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Day of Week</label>
                        <select
                          value={formData.scheduleDayOfWeek ?? 1}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, scheduleDayOfWeek: parseInt(e.target.value, 10) }))
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-ldaja-blue focus:border-transparent"
                        >
                          {DAYS_OF_WEEK.map((day, i) => (
                            <option key={day} value={i}>{day}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Form Error */}
              {formError && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex items-center justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-ldaja-blue text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Dialog ─────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Delete Automation Rule</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Test/Preview Modal ─────────────────────────────────────── */}
      {testTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ldaja-black">
                Test: {testTarget.name}
              </h2>
              <button
                onClick={closeTestModal}
                className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {testing && (
                <LoadingSpinner message="Running test..." size="sm" />
              )}

              {testError && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{testError}</p>
                </div>
              )}

              {testResult && (
                <div className="space-y-4">
                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-semibold text-ldaja-black mb-1">Would Send To</label>
                    <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700">
                      {Array.isArray(testResult.wouldSendTo)
                        ? testResult.wouldSendTo.join(', ')
                        : testResult.wouldSendTo}
                    </div>
                  </div>

                  {/* Variables Used */}
                  {Object.keys(testResult.variables).length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-ldaja-black mb-1">Template Variables</label>
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <div className="space-y-1">
                          {Object.entries(testResult.variables).map(([key, val]) => (
                            <div key={key} className="flex items-center gap-2 text-xs">
                              <code className="bg-gray-200 px-1.5 py-0.5 rounded text-gray-700">{`{{${key}}}`}</code>
                              <span className="text-gray-400">=</span>
                              <span className="text-gray-600 truncate">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Preview */}
                  <div>
                    <label className="block text-sm font-semibold text-ldaja-black mb-1">Email Preview</label>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-800">
                          Subject: {testResult.preview.subject}
                        </p>
                      </div>
                      <pre className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap font-sans">{testResult.preview.body}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-xl flex items-center justify-end">
              <button
                onClick={closeTestModal}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
